// src/store/slices/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { usersService } from '@/services/api/users';
import type {
  User,
  GetUsersQuery,
  Paginated,
  CreateUserRequest,
  UpdateUserRequest,
  AddPaymentCardRequest,
  InviteUsersInBulkRequest,
} from '@/services/api/users';

/** Small helper in case your apiClient wraps JSON under `.data` */
const unwrap = <T>(res: any): T => (res?.data ?? res) as T;

/* ================== State ================== */

export interface UsersState {
  items: User[];
  pagination: { total: number; page: number; limit: number };
  selectedUser: User | null;
  isLoading: boolean;
  error: string | null;
  message:string
}

const initialState: UsersState = {
  items: [],
  pagination: { total: 0, page: 1, limit: 10 },
  selectedUser: null,
  isLoading: false,
  error: null,
  message: ""
};

/* ================== Thunks ================== */

type UsersPayload = {
    items: User[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  };
  

  export const fetchUsers = createAsyncThunk<UsersPayload, any | undefined>(
    'users/fetchUsers',
    async (params, { rejectWithValue }) => {
      try {
        const res = await usersService.getUsers(params);

        const message = res.message

        const out: any = (res as any)?.data ?? res;
  
        const items: User[] =
          Array.isArray(out?.data) ? out.data :
          Array.isArray(out?.items) ? out.items :
          Array.isArray(out) ? out :
          [];
         
  
        const pagination = out?.pagination 
  
        return { items, pagination, message };
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to fetch users';
        return rejectWithValue(msg);
      }
    }
  );
  

export const fetchUserById = createAsyncThunk<User, string>(
  'users/fetchUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const res = await usersService.getUserById(userId);
      return unwrap<User>(res);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to fetch user';
      return rejectWithValue(msg);
    }
  }
);

export const createUser = createAsyncThunk<User, CreateUserRequest>(
  'users/createUser',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await usersService.createUser(payload);
      return unwrap<User>(res);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to create user';
      return rejectWithValue(msg);
    }
  }
);


export const inviteUsers = createAsyncThunk<any,any >(
  'users/invite-user',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await usersService.inviteUser(payload);
      return unwrap<User>(res);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to create user';
      return rejectWithValue(msg);
    }
  }
);

export const acceptInvite = createAsyncThunk<void, any>(
  'users/accept-invite',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await usersService.acceptInvite(payload);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to accept invite';
      return rejectWithValue(msg);
    }
  }
);


// Mirror your teams UpdateTeamBody pattern (ID in body)
export type UpdateUserBody = UpdateUserRequest;

// userSlice.ts (or slices/userSlice.ts)
export const updateUser = createAsyncThunk<User, { userId: string | number; data: any }>(
    'users/updateUser',
    async ({ userId, data }, { rejectWithValue }) => {
      try {
        const res = await usersService.updateUser(userId, data);
        return unwrap<User>(res);
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to update user';
        return rejectWithValue(msg);
      }
    }
  );
  
  export const deleteUser = createAsyncThunk<string | number, string | number>(
    'users/deleteUser',
    async (userId, { rejectWithValue }) => {
      try {
        await usersService.deleteUser(userId);
        return userId; // optional: use this to prune from state
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to delete user';
        return rejectWithValue(msg);
      }
    }
  );
  
export const addPaymentCardDetails = createAsyncThunk<void, AddPaymentCardRequest>(
  'users/addPaymentCardDetails',
  async (payload, { rejectWithValue }) => {
    try {
      await usersService.addPaymentCardDetails(payload);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to add payment card details';
      return rejectWithValue(msg);
    }
  }
);

/* ================== Slice ================== */

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearUserError(state) {
      state.error = null;
    },
    setSelectedUser(state, action: PayloadAction<User | null>) {
      state.selectedUser = action.payload;
    },
    resetUsers(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    // Fetch list
    builder
    .addCase(fetchUsers.pending, (s) => { s.isLoading = true; })
    .addCase(fetchUsers.fulfilled, (s, a) => {
      s.isLoading = false;
      s.items = a.payload.items;         // 👈 must write to .items
      s.pagination = a.payload.pagination;
      s.error = null;
    })
    .addCase(fetchUsers.rejected, (s, a) => {
      s.isLoading = false;
      s.error = String(a.payload ?? a.error?.message ?? 'Failed');
    });
  
      

    // Fetch by id
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload;
        const i = state.items.findIndex((u) => u.id === action.payload.id);
        if (i >= 0) state.items[i] = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create
    builder
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update
    builder
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const idx = state.items.findIndex((u) => u.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete
    builder
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.filter((u) => u.id !== action.payload);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        if (state.selectedUser?.id === action.payload) state.selectedUser = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Add payment card (no state change besides flags)
    builder
      .addCase(addPaymentCardDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPaymentCardDetails.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addPaymentCardDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearUserError, setSelectedUser, resetUsers } = usersSlice.actions;
export default usersSlice.reducer;
